import classNames from "classnames";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";
import { Column, useTable } from "react-table";
import { cn } from "@/lib/utils";

export interface TableProps {
  columns: Array<Column<any>>;
  data?: Array<any>;
  loading?: boolean;
  emptyState?: ReactNode;
  striped?: boolean;
  hideHeader?: boolean;
}

export function Table({
  columns,
  data,
  loading,
  emptyState = undefined,
  striped = false,
  hideHeader = false,
}: TableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: data || [],
  });

  if (loading) {
    return <TableLoading />;
  }
  if (!rows.length) {
    return <TableEmpty emptyState={emptyState} />;
  }

  return (
    <table className="min-w-full" {...getTableProps()}>
      <thead
        className={classNames(
          {
            hidden: hideHeader,
          },
          "border-b border-t border-border relative z-[2]",
        )}
      >
        {headerGroups.map((headerGroup) => (
          // eslint-disable-next-line react/jsx-key
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              // eslint-disable-next-line react/jsx-key
              <th
                scope="col"
                className="text-muted-foreground px-6 py-3 text-xs font-medium text-left"
                style={column.width ? { width: column.width } : {}}
                {...column.getHeaderProps()}
              >
                {column.render("Header")}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody
        className="bg-background divide-border border-border relative min-h-screen border-b divide-y"
        {...getTableBodyProps()}
      >
        {rows.map((row) => {
          prepareRow(row);
          return (
            // eslint-disable-next-line react/jsx-key
            <tr
              className={classNames("last:rounded-b-lg first:rounded-t-lg", {
                "bg-background": !striped || (striped && row.index % 2 === 0),
                "bg-muted": striped && row.index % 2 !== 0,
              })}
              {...row.getRowProps()}
            >
              {row.cells.map((cell, _idx) => (
                // eslint-disable-next-line react/jsx-key
                <td
                  className={classNames(
                    `px-3 sm:px-4 md:px-5 lg:px-6 py-4 whitespace-nowrap text-sm`,
                    {
                      "first:rounded-tl-lg last:rounded-tr-lg": row.index === 0,
                      "first:rounded-bl-lg last:rounded-br-lg":
                        row.index === (data?.length ?? 0) - 1,
                    },
                  )}
                  {...cell.getCellProps()}
                >
                  {cell.render("Cell")}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function TableEmpty(props: { emptyState: ReactNode }) {
  return (
    <div className="bg-background border-border px-4 py-16 border rounded-md">
      <span className="text-muted-foreground block mt-2 text-sm">
        {props.emptyState || <p className="text-muted-foreground block mt-2 text-sm">No Results</p>}
      </span>
    </div>
  );
}

export function TableLoading({ className = "" }: { className?: string }) {
  return (
    <div
      className={cn("flex flex-col items-center px-4 py-16 bg-background rounded-md", className)}
    >
      <Loader2 className="animate-spin text-muted w-16 h-16" />
      <p className="mt-4 text-center">Loading data...</p>
    </div>
  );
}
